/**
 * Singleton session manager for the AI agent WebSocket connection.
 *
 * Manages:
 * - WebSocket lifecycle (connect, disconnect, reconnect)
 * - Browser tab subscription (subscribe/unsubscribe to gateway)
 * - TTL auto-expiration (30 min without activity)
 * - Page reload recovery via sessionStorage
 * - State change notifications to AgentToggle and ChatDrawer
 *
 * The ChatDrawer depends on this: WS active = chat possible, WS off = chat closes.
 */

import Config from '@/shared/Config'
import { executeDOMAction } from './domExecutor'

const TTL_MS = 30 * 60 * 1000 // 30 minutes
const RECONNECT_DELAYS = [1000, 2000, 4000] // exponential backoff
const HEARTBEAT_INTERVAL = 5 * 60 * 1000 // 5 minutes

const STORAGE = {
  ACTIVE: 'ai_agent_active',
  ACTIVATED_AT: 'ai_agent_activated_at'
}

let _instance = null

class AgentSession {
  constructor () {
    this._ws = null
    this._active = false
    this._ttlTimer = null
    this._heartbeatTimer = null
    this._reconnectAttempt = 0
    this._sessionId = null
    this._lastToolUseId = null
    this._listeners = []
    this._chatListeners = []
    this._chatOpen = false
    this._module = ''
    this._route = ''
  }

  // --- Public API ---

  /**
   * Activate the agent session: connect WS, subscribe, start TTL.
   * Called when: user clicks toggle ON, or ChatDrawer opens.
   */
  async activate (sessionId) {
    if (this._active && this._ws) return

    this._sessionId = sessionId || this._sessionId
    if (!this._sessionId) {
      console.warn('[AgentSession] No sessionId provided')
      return
    }

    this._active = true
    sessionStorage.setItem(STORAGE.ACTIVE, 'true')
    sessionStorage.setItem(STORAGE.ACTIVATED_AT, String(Date.now()))

    await this._connect()
    this._startTTL(TTL_MS)
    this._notify()
  }

  /**
   * Deactivate: close WS, clear TTL, clear storage, notify listeners.
   * Called when: user clicks toggle OFF, TTL expires, 3 reconnect failures.
   */
  deactivate () {
    this._active = false
    this._closeWS()
    this._clearTTL()
    this._clearHeartbeat()
    sessionStorage.removeItem(STORAGE.ACTIVE)
    sessionStorage.removeItem(STORAGE.ACTIVATED_AT)
    this._notify()
  }

  /**
   * Toggle on/off.
   */
  async toggle (sessionId) {
    if (this._active) {
      this.deactivate()
    } else {
      await this.activate(sessionId)
    }
  }

  /**
   * Attempt to recover session after page reload.
   * Returns true if recovery succeeded (WS reconnected).
   */
  async tryRecover (sessionId) {
    this._sessionId = sessionId

    if (sessionStorage.getItem(STORAGE.ACTIVE) !== 'true') {
      return false
    }

    const activatedAt = parseInt(sessionStorage.getItem(STORAGE.ACTIVATED_AT) || '0', 10)
    const elapsed = Date.now() - activatedAt
    const remaining = TTL_MS - elapsed

    if (remaining <= 0) {
      // TTL expired during reload
      sessionStorage.removeItem(STORAGE.ACTIVE)
      sessionStorage.removeItem(STORAGE.ACTIVATED_AT)
      return false
    }

    // TTL still valid → reconnect
    this._active = true
    await this._connect()
    this._startTTL(remaining)
    this._notify()
    return true
  }

  /**
   * Reset TTL timer (called on each ui_action received).
   */
  resetTTL () {
    this._startTTL(TTL_MS)
    sessionStorage.setItem(STORAGE.ACTIVATED_AT, String(Date.now()))
  }

  /**
   * Subscribe to state changes.
   * Callback receives: { active: bool, wsConnected: bool }
   */
  onChange (callback) {
    this._listeners.push(callback)
  }

  /**
   * Request the chat panel to toggle (called from header button).
   */
  requestOpenChat () {
    for (const cb of this._chatListeners) {
      try { cb() } catch { /* ignore */ }
    }
  }

  /**
   * Notify that the chat panel open state changed (called from ChatDrawer).
   */
  setChatOpen (isOpen) {
    this._chatOpen = isOpen
    this._notify()
  }

  /**
   * Subscribe to chat open requests (called from ChatDrawer).
   */
  onOpenChat (callback) {
    this._chatListeners.push(callback)
  }

  /**
   * Current state.
   */
  isActive () {
    return this._active
  }

  isConnected () {
    return this._ws !== null && this._ws.readyState === WebSocket.OPEN
  }

  // --- WebSocket ---

  async _connect () {
    const url = await this._getWsUrl()
    if (!url) {
      console.warn('[AgentSession] Cannot determine WS URL')
      return
    }

    console.debug('[AgentSession] Connecting to', url)
    const socket = new WebSocket(url)

    socket.onopen = async () => {
      console.debug('[AgentSession] Connected')
      this._ws = socket
      this._reconnectAttempt = 0

      // Authenticate with the gateway
      await this._sendAuth(socket)

      // Subscribe this tab
      this._updateModuleAndRoute()
      socket.send(JSON.stringify({
        type: 'subscribe',
        module: this._module,
        route: this._route
      }))

      this._startHeartbeat()
      this._notify()
    }

    socket.onmessage = async (event) => {
      let msg
      try {
        msg = JSON.parse(event.data)
      } catch {
        return
      }

      if (msg.type === 'ui_action') {
        this.resetTTL()
        const toolUse = msg.tool_use
        this._lastToolUseId = toolUse.id
        try {
          const result = await executeDOMAction(toolUse.name, toolUse.input)

          if (result && result.crossModuleNavigation) {
            // Cross-module: close WS, navigate, WS will reconnect on new page
            this._closeWS()
            window.location.href = result.navigateTo
            return
          }

          socket.send(JSON.stringify({
            type: 'ui_result',
            tool_use_id: toolUse.id,
            result: { success: true, data: result }
          }))
        } catch (err) {
          socket.send(JSON.stringify({
            type: 'ui_result',
            tool_use_id: toolUse.id,
            result: { success: false, error: String(err) }
          }))
        } finally {
          this._lastToolUseId = null
        }
      } else if (msg.type === 'subscribed') {
        console.debug('[AgentSession] Subscribed, TTL:', msg.ttl_seconds, 's')
      } else if (msg.type === 'auth_ok') {
        console.debug('[AgentSession] Auth OK')
      } else if (msg.type === 'auth_error') {
        console.warn('[AgentSession] Auth failed:', msg.error)
        this.deactivate()
      }
    }

    socket.onclose = (event) => {
      this._ws = null
      this._clearHeartbeat()

      if (this._active && event.code !== 1000) {
        // Unexpected close → retry
        this._retryConnect()
      }

      this._notify()
    }

    socket.onerror = () => {
      // onclose will fire after this
    }
  }

  _retryConnect () {
    if (this._reconnectAttempt >= RECONNECT_DELAYS.length) {
      console.warn('[AgentSession] Max reconnect attempts reached, deactivating')
      this.deactivate()
      return
    }

    const delay = RECONNECT_DELAYS[this._reconnectAttempt]
    this._reconnectAttempt++
    console.debug('[AgentSession] Retry in', delay, 'ms (attempt', this._reconnectAttempt, ')')
    setTimeout(() => {
      if (this._active) this._connect()
    }, delay)
  }

  _closeWS () {
    if (this._ws) {
      try {
        if (this._active) {
          this._ws.send(JSON.stringify({ type: 'unsubscribe' }))
        }
        this._ws.close(1000)
      } catch { /* ignore */ }
      this._ws = null
    }
  }

  async _sendAuth (socket) {
    try {
      const store = window.__fpui_store__
      const session = store?.getters?.SESSION || store?.getters?.KING_SESSION
      const token = session?.token || ''
      // Derive project_url from the current page origin or config
      const config = await Config()
      const projectUrl = config?.BASE_URL || window.location.origin
      if (token) {
        socket.send(JSON.stringify({
          type: 'auth',
          token: `Bearer ${token}`,
          project_url: projectUrl
        }))
      }
    } catch (err) {
      console.warn('[AgentSession] Failed to send auth:', err)
    }
  }

  async _getWsUrl () {
    // Derive from config BASE_URL — same pattern as all other APIs
    // e.g. BASE_URL = "https://finops.forepaas.io" → wss://finops.forepaas.io/mcp/ws/{session}
    const config = await Config()
    if (!config.BASE_URL) return null
    const wsBase = config.BASE_URL
      .replace('https://', 'wss://')
      .replace('http://', 'ws://')
    return `${wsBase}/mcp/ws/${this._sessionId}`
  }

  // --- TTL ---

  _startTTL (duration) {
    this._clearTTL()
    this._ttlTimer = setTimeout(() => {
      console.debug('[AgentSession] TTL expired, deactivating')
      this.deactivate()
    }, duration)
  }

  _clearTTL () {
    if (this._ttlTimer) {
      clearTimeout(this._ttlTimer)
      this._ttlTimer = null
    }
  }

  // --- Heartbeat ---

  _startHeartbeat () {
    this._clearHeartbeat()
    this._heartbeatTimer = setInterval(() => {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ type: 'heartbeat' }))
      }
    }, HEARTBEAT_INTERVAL)
  }

  _clearHeartbeat () {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer)
      this._heartbeatTimer = null
    }
  }

  // --- Route tracking ---

  _updateModuleAndRoute () {
    this._module = window.location.pathname.replace(/^\/+/, '').split('/')[0] || ''
    this._route = window.location.hash || '#/'
  }

  /**
   * Notify the gateway that the user navigated to a different page.
   * Called by the Vue router afterEach hook.
   */
  notifyRouteChanged () {
    this._updateModuleAndRoute()
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({
        type: 'route_changed',
        module: this._module,
        route: this._route
      }))
    }
  }

  // --- Notifications ---

  _notify () {
    const state = {
      active: this._active,
      wsConnected: this.isConnected(),
      chatOpen: this._chatOpen
    }
    for (const cb of this._listeners) {
      try { cb(state) } catch { /* ignore */ }
    }
  }
}

/**
 * Get the singleton AgentSession instance.
 */
export function getAgentSession () {
  if (!_instance) {
    _instance = new AgentSession()
  }
  return _instance
}
